const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 30000;

// Opens an SSE stream that keeps itself alive. The browser retries a dropped stream on its
// own, but once it reaches CLOSED (e.g. the server answered with an error while the network
// was coming back) it gives up for good — so reopen it ourselves with backoff, and straight
// away when the browser reports the network is back. Returns a function that closes it.
export function openResilientEventSource(url, { listeners = {}, onOpen, onError } = {}) {
    let source = null;
    let retryTimer = null;
    let attempt = 0;
    let stopped = false;

    const scheduleRetry = () => {
        if (stopped || retryTimer) return;
        const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** attempt);
        attempt += 1;
        retryTimer = window.setTimeout(connect, delay);
    };

    function connect() {
        window.clearTimeout(retryTimer);
        retryTimer = null;
        source?.close();

        const current = new EventSource(url, { withCredentials: true });
        source = current;
        current.onopen = () => {
            attempt = 0;
            onOpen?.();
        };
        current.onerror = () => {
            onError?.();
            if (current.readyState === EventSource.CLOSED) scheduleRetry();
        };
        Object.entries(listeners).forEach(([name, handler]) => current.addEventListener(name, handler));
    }

    const handleOnline = () => {
        if (stopped || source?.readyState === EventSource.OPEN) return;
        attempt = 0;
        connect();
    };

    window.addEventListener('online', handleOnline);
    connect();

    return () => {
        stopped = true;
        window.clearTimeout(retryTimer);
        window.removeEventListener('online', handleOnline);
        source?.close();
    };
}
